import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import AccountTypeModal from "./AccountTypeModal";
import { persistForumUser, resolveGoogleLogin } from "../utils/googleAuth";
import "./Login.css";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pendingGoogle, setPendingGoogle] = useState(null);
  const [accountTypeLoading, setAccountTypeLoading] = useState(false);
  const navigate = useNavigate();

  const URL = process.env.REACT_APP_API_URL;

  const finishWithUser = (user) => {
    persistForumUser(user);
    if (user.role === "admin") {
      window.location.href = "/admin";
    } else {
      onLogin(user);
    }
  };

  // --- Normal login ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const res = await fetch(`${URL}/login.php`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ email, password }).toString(),
      });

      const data = await res.json();

      if (data.success && data.user) {
        finishWithUser(data.user);
      } else {
        alert(data.message || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      alert("Error: " + error.message);
    }
  };

  // --- Google login ---
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const googleProfile = {
        credential: credentialResponse.credential,
        google_id: decoded.sub,
        email: decoded.email,
        name: decoded.name || "",
        avatar: decoded.picture || "",
      };

      const result = await resolveGoogleLogin(googleProfile);

      if (result.needsAccountType) {
        setPendingGoogle(result.pending);
        return;
      }

      if (result.user) {
        finishWithUser(result.user);
      } else {
        alert(result.error || "Google login failed");
      }
    } catch (err) {
      console.error("Google login failed:", err);
    }
  };

  const handleAccountTypeConfirm = async ({ typeofuser, password, country, position }) => {
    if (!pendingGoogle) return;
    setAccountTypeLoading(true);
    try {
      const result = await resolveGoogleLogin(pendingGoogle, {
        typeofuser,
        password,
        country,
        position,
      });
      if (result.user) {
        setPendingGoogle(null);
        finishWithUser(result.user);
      } else {
        alert(result.error || "Could not create your account.");
      }
    } catch (err) {
      console.error("Google signup failed:", err);
      alert("Could not create your account.");
    } finally {
      setAccountTypeLoading(false);
    }
  };

  return (
    <div className="login-page">
      <AccountTypeModal
        open={!!pendingGoogle}
        email={pendingGoogle?.email}
        loading={accountTypeLoading}
        onConfirm={handleAccountTypeConfirm}
        onCancel={() => setPendingGoogle(null)}
      />

      <div className="login-container">
        <div className="login-card">
          <h2 className="login-title">Welcome back</h2>
          <p className="login-subtitle">
            Sign in to continue the discussion and explore what’s new.
          </p>

          {/* Normal login form */}
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-field">
              <label className="login-label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className="login-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="login-field">
              <label className="login-label" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                className="login-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button type="submit" className="login-button">
              Login
            </button>
          </form>

          <div className="login-divider">
            <div className="login-divider-line" />
            <span className="login-divider-text">Or continue with</span>
            <div className="login-divider-line" />
          </div>

          {/* Google login button */}
          <div className="login-google-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => console.log("Google login failed")}
            />
          </div>

          <div className="login-footer">
            <p>
              Don’t have an account?{" "}
              <button type="button" onClick={() => navigate("/register")}>
                Register here
              </button>
            </p>
            <p style={{ marginTop: "0.5rem" }}>
              Or{" "}
              <Link to="/">
                Visit the forum without login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
